import React, { useState, useEffect } from 'react';
import { createTrade, getAdminStats } from '../../services/api';
import toast from 'react-hot-toast';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

const TradeEntry = () => {
  const [formData, setFormData] = useState({
    coin_ticker: 'BTC',
    direction: 'LONG',
    realized_pnl_usdt: '',
    entry_price_usdt: '',
    exit_price_usdt: '',
    notes: '',
  });
  const [stats, setStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getAdminStats();
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.realized_pnl_usdt || isNaN(formData.realized_pnl_usdt)) {
      toast.error('Please enter a valid PnL amount');
      return;
    }

    const pnl = parseFloat(formData.realized_pnl_usdt);
    if (pnl === 0) {
      toast.error('PnL cannot be zero');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createTrade({
        coin_ticker: formData.coin_ticker,
        direction: formData.direction,
        realized_pnl_usdt: pnl,
        entry_price_usdt: formData.entry_price_usdt ? parseFloat(formData.entry_price_usdt) : null,
        exit_price_usdt: formData.exit_price_usdt ? parseFloat(formData.exit_price_usdt) : null,
        notes: formData.notes,
      });

      if (response.data.success) {
        toast.success(`Trade recorded! PnL allocated to ${stats?.total_users || 0} users`);
        setFormData((prev) => ({
          ...prev,
          realized_pnl_usdt: '',
          entry_price_usdt: '',
          exit_price_usdt: '',
          notes: '',
        }));
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create trade');
    } finally {
      setSubmitting(false);
    }
  };

  const coinOptions = ['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'USDT', 'USDC'];

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">Manual Trade Entry</h1>
        <p className="dashboard-section-subtitle">Record trade results and allocate P&amp;L across users</p>
      </div>

      {stats && (
        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Current Pool Stats</h2>
            <p className="dashboard-section-subtitle">Live allocation overview</p>
          </div>

          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-body">
                <p className="admin-stat-label">Total Users</p>
                <p className="admin-stat-value">{stats.total_users}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-body">
                <p className="admin-stat-label">Total Deposits</p>
                <p className="admin-stat-value text-emerald-500">${stats.total_deposits?.toFixed(2)}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-body">
                <p className="admin-stat-label">Current Pool</p>
                <p className="admin-stat-value text-blue-400">
                  ${(stats.total_deposits - stats.total_withdrawals).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Enter Trade Result</h2>
          <p className="dashboard-section-subtitle">
            This will allocate P&amp;L to all users proportionally. Double-check before submitting.
          </p>
        </div>

        <div className="dashboard-section-body">
          <div className="trade-entry-warning">
            ⚠️ IMPORTANT: This will allocate P&amp;L to ALL users proportionally. Double-check before submitting.
          </div>

          <form onSubmit={handleSubmit} className="trade-entry-form">
            <div className="trade-entry-grid">
              <div className="auth-field">
                <label className="auth-label">Coin / Asset</label>
                <select
                  value={formData.coin_ticker}
                  onChange={(e) => setFormData({ ...formData, coin_ticker: e.target.value })}
                  className="auth-input"
                  disabled={submitting}
                >
                  {coinOptions.map((coin) => (
                    <option key={coin} value={coin}>
                      {coin}
                    </option>
                  ))}
                </select>
              </div>

              <div className="auth-field">
                <label className="auth-label">Direction</label>
                <div className="trade-direction-row">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, direction: 'LONG' })}
                    className={`trade-direction-btn ${formData.direction === 'LONG' ? 'long active' : ''}`}
                  >
                    <ArrowTrendingUpIcon className="w-4 h-4" />
                    LONG
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, direction: 'SHORT' })}
                    className={`trade-direction-btn ${formData.direction === 'SHORT' ? 'short active' : ''}`}
                  >
                    <ArrowTrendingDownIcon className="w-4 h-4" />
                    SHORT
                  </button>
                </div>
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Realized PnL (USDT)</label>
              <input
                type="number"
                value={formData.realized_pnl_usdt}
                onChange={(e) => setFormData({ ...formData, realized_pnl_usdt: e.target.value })}
                className="auth-input"
                placeholder="Enter positive for profit, negative for loss"
                step="0.01"
                required
              />
              <p className="auth-helper-text">Example: 500 for profit, -200 for loss</p>
            </div>

            <div className="trade-entry-grid">
              <div className="auth-field">
                <label className="auth-label">Entry Price (Optional)</label>
                <input
                  type="number"
                  value={formData.entry_price_usdt}
                  onChange={(e) => setFormData({ ...formData, entry_price_usdt: e.target.value })}
                  className="auth-input"
                  placeholder="e.g., 50000"
                  step="0.01"
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Exit Price (Optional)</label>
                <input
                  type="number"
                  value={formData.exit_price_usdt}
                  onChange={(e) => setFormData({ ...formData, exit_price_usdt: e.target.value })}
                  className="auth-input"
                  placeholder="e.g., 51000"
                  step="0.01"
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="auth-input trade-notes-textarea"
                placeholder="Add any notes about this trade..."
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn trade-submit-btn"
              disabled={submitting}
            >
              {submitting ? 'Processing Allocation...' : 'Submit Trade & Allocate PnL'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default TradeEntry;